import styled, { css } from 'styled-components';
import type { WorkflowStageTone } from '../../../hooks/useWorkflowStages';

export const StepperBar = styled.div`
  display: flex;
  width: 100%;
  height: 54px;
  padding: 0 16px;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  /* No overflow at the bar level — only the inner StepsRow scrolls, so the
     back/next arrows stay pinned at the edges regardless of viewport width. */
  overflow: hidden;
  position: sticky;
  /* Pinned just below the AppTopBar (47px tall, sticky at top: 0) */
  top: 47px;
  z-index: 9;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #909296;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    background: #f1f3f5;
    color: #228be6;
  }

  &:disabled {
    color: #ced4da;
    cursor: not-allowed;
  }
`;

/**
 * Full-height vertical divider that separates the back/forward arrows from
 * the steps row. Stretches the entire stepper height by aligning to the
 * `StepperBar` flex line (which has `align-items: center` for the rest of
 * the chrome but lets stretch children take the full track via align-self).
 */
export const ArrowDivider = styled.span`
  display: inline-block;
  width: 1px;
  align-self: stretch;
  background: #e9ecef;
  flex-shrink: 0;
`;

export const StepsRow = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  /* Horizontal scroll inside the back/next arrows when the viewport is too
     narrow to lay every step out side-by-side. Steps keep their natural
     width — they no longer compress into illegible chips. */
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dee2e6;
    border-radius: 2px;
  }
`;

export const StepItemWrap = styled.li`
  display: flex;
  align-items: center;
  /* Grow to fill the row's spare width (the Connector inside each wrap
     absorbs the extra space, stretching evenly between labels) but never
     shrink below the wrap's natural width. With flex-shrink=0 the labels
     keep their full width at every viewport; StepsRow's overflow-x then
     handles the case where natural width > container. The last wrap has
     no Connector after it, so it stays at natural width to avoid trailing
     whitespace at the right edge of the row. */
  flex: 1 0 auto;

  &:last-child {
    flex: 0 0 auto;
  }
`;

const toneColors = {
  success: '#12b886',
  info: '#228be6',
  warning: '#fab005',
  error: '#fa5252',
  neutral: '#adb5bd'
} as const;

export const StepButton = styled.button<{ $disabled?: boolean; $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  text-align: left;
  min-width: 0;
  font: inherit;
  transition: background 0.15s ease;

  @media (max-width: 1199px) {
    padding: 6px 4px;
  }

  > .label {
    color: #101113;
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 140%;
    white-space: nowrap;
  }

  /* Sub-label typography now lives on the SubLabel styled-component
     itself. The shortcut-icon feature nests SubLabel inside SubLabelRow,
     so a direct-child selector for .sub-label can't reach it here. */

  &:not(:disabled):hover > .label {
    text-decoration: underline;
  }
`;

export const StepHead = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  /* Active pill — soft gray rounded rectangle wrapping the whole step. */
  ${({ $active }) =>
    $active &&
    css`
      background: #f1f3f5;
      box-shadow: inset 0 0 0 1px #e9ecef;

      > button {
        background: transparent;
      }
    `}
`;

interface ConnectorProps {
  $tone: 'done' | 'pending';
}

export const Connector = styled.span<ConnectorProps>`
  display: inline-block;
  /* Grows to absorb the row's slack at wide widths, never shrinks below
     12px. Combined with StepsRow's overflow-x, the row keeps a sensible
     look at every width: stretched when there's room, scrollable when not.
     Narrow + tight margins keep adjacent step labels visually close. */
  flex: 1 1 12px;
  min-width: 12px;
  height: 1px;
  margin: 0 4px;
  background: ${({ $tone }) => ($tone === 'done' ? toneColors.info : '#dee2e6')};
  ${({ $tone }) =>
    $tone === 'pending' &&
    css`
      background-image: linear-gradient(to right, #dee2e6 50%, transparent 50%);
      background-size: 8px 1px;
      background-repeat: repeat-x;
      background-color: transparent;
    `}
`;

export const SubLabel = styled.span<{ $tone?: WorkflowStageTone }>`
  font-family: Roboto, sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 140%;
  white-space: nowrap;
  color: ${({ $tone }) => {
    if ($tone === 'success') return toneColors.success;
    if ($tone === 'warning') return toneColors.warning;
    if ($tone === 'info') return toneColors.info;
    if ($tone === 'error') return toneColors.error;
    return '#868e96';
  }};
`;

// Inline row that pairs the sub-label text with any per-stage shortcut
// button (e.g. "Copy jurisdictions from previous period"). Keeps the icon
// vertically centred with the sub-label baseline.
export const SubLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #ffffff;
  color: ${toneColors.info};
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    background: #e7f5ff;
    border-color: ${toneColors.info};
    color: #1c7ed6;
    box-shadow: 0 1px 2px rgba(34, 139, 230, 0.15);
  }

  &:active:not(:disabled) {
    background: #d0ebff;
    box-shadow: inset 0 1px 2px rgba(34, 139, 230, 0.2);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;
